import { authController } from "../../../AppController";
import { APIRoutes } from "../../../config";
import { getApiBaseUrl } from "../../../pages/Neo/apiBase";
import type { NeoConversationRichContentPart } from "../../../hexagon/core/ports/driven/neoConversationRepository";
import type { CopilotMessage } from "./types";

// Copy / Download helpers for assistant replies, ported from the old Neo page
// (src/pages/Neo/index.tsx) so both surfaces export the same files.

export type NeoDocumentFormat = "pdf" | "xlsx";

export type NeoPdfContentPart =
  | { type: "text"; text: string }
  | NeoConversationRichContentPart;

const FORMAT_INFO: Record<
  NeoDocumentFormat,
  { route: string; mimeType: string; extension: string; label: string }
> = {
  pdf: {
    route: APIRoutes.apiNeoDocumentRenderPdf,
    mimeType: "application/pdf",
    extension: "pdf",
    label: "PDF",
  },
  xlsx: {
    route: APIRoutes.apiNeoDocumentRenderXlsx,
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    extension: "xlsx",
    label: "Excel",
  },
};

export const formatLabel = (format: NeoDocumentFormat): string =>
  FORMAT_INFO[format].label;

const HEREDOC_OPEN = /^<<HEREDOC\s*$/m;

const stripHeredocDelimiters = (text: string): string =>
  text
    .replace(/^<<HEREDOC[^\S\r\n]*(?:\r?\n|$)/gm, "")
    .replace(/^HEREDOC[^\S\r\n]*(?:\r?\n|$)/gm, "");

/** Heredoc documents render their own card with its own download. */
export const containsHeredoc = (message: CopilotMessage): boolean =>
  HEREDOC_OPEN.test(message.content);

export const messageText = (message: CopilotMessage): string =>
  stripHeredocDelimiters(message.content.trim()).trim();

export const countWords = (text: string): number =>
  text.trim() ? text.trim().split(/\s+/).length : 0;

/** Text plus chart parts, for replies whose charts should land in the export.
 *  Undefined when there is no chart — plain markdown export covers that. */
export const messagePdfContentParts = (
  message: CopilotMessage
): NeoPdfContentPart[] | undefined => {
  const charts = (message.richContent ?? []).filter(
    (part) => part.type === "neo_chart"
  );
  if (charts.length === 0) return undefined;
  const text = messageText(message);
  return [...(text ? [{ type: "text" as const, text }] : []), ...charts];
};

export const hasMarkdownTable = (markdown: string): boolean =>
  /^\s*\|?.+\|.+\s*$\r?\n\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/m.test(
    markdown
  );

const extractRawBodyJson = (text: string): string | null => {
  const trimmed = text.trim();
  if (!trimmed || !/^[[{]/.test(trimmed)) return null;
  try {
    JSON.parse(trimmed);
    return trimmed;
  } catch {
    return null;
  }
};

const ALL_JSON_FENCES_RE = /```[ \t]*json\b[^\n]*\n([\s\S]*?)```/gi;

/** A raw-JSON reply, or one wrapping exactly one ```json fence. Two or more
 *  fences are ambiguous, so those fall back to PDF. */
export const extractMessageJson = (text: string): string | null => {
  const raw = extractRawBodyJson(text);
  if (raw) return raw;
  const fences = [...text.matchAll(ALL_JSON_FENCES_RE)];
  if (fences.length !== 1) return null;
  const inner = fences[0][1].replace(/\s+$/, "");
  return inner.trim() ? inner : null;
};

export const documentTitle = (markdown: string): string => {
  const heading = markdown.match(/^\s*#{1,3}\s+(.+?)\s*$/m)?.[1];
  return heading
    ? heading.replace(/\*\*/g, "").replace(/\*/g, "").replace(/`/g, "").trim()
    : "Neo Document";
};

const contentPartsTitle = (parts: NeoPdfContentPart[]): string => {
  for (const part of parts) {
    if (part.type === "text" && typeof part.text === "string") {
      const title = documentTitle(part.text);
      if (title !== "Neo Document") return title;
    }
  }
  const chart = parts.find(
    (part): part is NeoConversationRichContentPart => part.type === "neo_chart"
  );
  const chartTitle = chart?.title;
  return typeof chartTitle === "string" && chartTitle.trim()
    ? chartTitle.trim()
    : "Neo Document";
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64) || "neo-document";

const SOURCE_TOKEN_RE = /\[\[source:([a-zA-Z0-9_:.-]+)\]\]/g;

/** Citation chips are in-app only; strip them from anything we export. */
const stripSourceMarkers = (text: string): string =>
  text
    .split("\n")
    .map((line) => {
      if (!line.includes("[[source:")) return line;
      const lead = (line.match(/^[ \t]*/) ?? [""])[0];
      const body = line
        .slice(lead.length)
        .replace(SOURCE_TOKEN_RE, "")
        .replace(/[ \t]+([.,;:!?])/g, "$1")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/[ \t]+$/g, "");
      return `${lead}${body}`;
    })
    .join("\n");

const contentDispositionFilename = (
  header: string | null,
  fallback: string
): string => {
  if (!header) return fallback;
  const star = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through */
    }
  }
  return header.match(/filename\s*=\s*"?([^";]+)"?/i)?.[1] ?? fallback;
};

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.rel = "noopener";
  // Appending is required in Firefox for a programmatic click to dispatch.
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
};

/** Render markdown or text+chart content parts server-side and save it. */
export const downloadNeoDocument = async (
  source: { markdown: string } | { contentParts: NeoPdfContentPart[] },
  format: NeoDocumentFormat
): Promise<void> => {
  const info = FORMAT_INFO[format];
  const title =
    "markdown" in source
      ? documentTitle(source.markdown)
      : contentPartsTitle(source.contentParts);
  const body =
    "markdown" in source
      ? { markdown: stripSourceMarkers(source.markdown), title }
      : {
          content_parts: source.contentParts.map((part) =>
            part.type === "text" && typeof part.text === "string"
              ? { ...part, text: stripSourceMarkers(part.text) }
              : part
          ),
          title,
        };
  const token = await authController.getAuthenticationToken();
  const response = await fetch(getApiBaseUrl() + info.route, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: info.mimeType,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const errorBody = await response.json();
      if (errorBody && typeof errorBody.detail === "string") {
        detail = errorBody.detail;
      }
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  triggerBlobDownload(
    await response.blob(),
    contentDispositionFilename(
      response.headers.get("content-disposition"),
      `${slugify(title)}.${info.extension}`
    )
  );
};

export const downloadJsonText = (json: string): void =>
  triggerBlobDownload(
    new Blob([json], { type: "application/json" }),
    "data.json"
  );
